import React from 'react';
import { useHistory } from 'react-router-dom';

export function useLocationQueryState(queryParamName: string, defaultValue?: string) {
	const history = useHistory();
	const { location } = history;
	const searchParams = new URLSearchParams(location.search);
	const value = searchParams.get(queryParamName) ?? defaultValue;
	const [queryParamValue, setQueryParamValue] = React.useState(value);
	const setValue = React.useCallback(
		(tabKey: string | undefined, options?: { clearExtraParams?: string[] }) => {
			const nextSearchValue = new URLSearchParams(location.search);

			nextSearchValue.delete(queryParamName);

			// Clear additional params in the same URL update
			if (options?.clearExtraParams) {
				options.clearExtraParams.forEach(param => nextSearchValue.delete(param));
			}

			if (tabKey) {
				nextSearchValue.set(queryParamName, tabKey);
			}
			setQueryParamValue(tabKey);

			// Use React Router's history.replace to keep router state in sync
			history.replace({
				pathname: location.pathname,
				search: nextSearchValue.toString(),
				hash: location.hash,
			});
		},
		[location, queryParamName, history]
	);

	React.useEffect(() => {
		const initialPathname = history.location.pathname;

		const unlisten = history.listen(currentLocation => {
			const currentSearchValues = new URLSearchParams(currentLocation.search);
			if (currentSearchValues.has(queryParamName)) {
				setQueryParamValue(currentSearchValues.get(queryParamName)?.toString());
			}
		});

		return () => {
			unlisten();
			// Only clean up if we're still on the same pathname
			// If pathname changed, we've navigated away and shouldn't touch the new route's params
			if (history.location.pathname === initialPathname) {
				const currentSearchParams = new URLSearchParams(history.location.search);
				currentSearchParams.delete(queryParamName);

				history.replace({
					pathname: history.location.pathname,
					search: currentSearchParams.toString(),
					hash: history.location.hash,
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return [queryParamValue, setValue] as const;
}
